import React, { useState } from "react";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core";

export const REJECT_REASONS = [
  "Different root cause",
  "Different application or service",
  "Similar symptom, unrelated cause",
  "Already fixed / not the same failure mode",
  "Insufficient evidence in the match",
  "Other",
];

// Latest verdict wins when a reviewer changes their mind on the same match.
export function getLatestVerdict(feedback, matchIncidentId) {
  if (!Array.isArray(feedback) || !matchIncidentId) return null;
  const entries = feedback.filter((entry) => entry.matchIncidentId === matchIncidentId);
  return entries.length ? entries[entries.length - 1] : null;
}

export function getPostmortemReportPath(match) {
  if (!match?.incidentId || !match?.postMortemId) return null;
  const base = process.env.REACT_APP_REDIRECT_URL || "";
  return `${base}/report?incidentID=${encodeURIComponent(match.incidentId)}&postmortemID=${encodeURIComponent(match.postMortemId)}&page_type=report`;
}

export function MatchTitle({ match }) {
  const reportPath = getPostmortemReportPath(match);
  if (!reportPath) {
    return <>{[match.incidentId, match.postMortemId].filter(Boolean).join(" / ")}</>;
  }
  return (
    <a href={reportPath} target="_blank" rel="noopener noreferrer" title="Open this postmortem in a new tab">
      {match.incidentId} / {match.postMortemId}
    </a>
  );
}

function RecurrenceMatchFeedback({ match, verdict, disabled, onAccept, onReject }) {
  const [rejecting, setRejecting] = useState(false);
  const [changing, setChanging] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");

  const decided = verdict?.verdict;

  if (decided && !changing && !rejecting) {
    const accepted = decided === "ACCEPTED";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600, fontSize: "13px", color: accepted ? "#2c8a4d" : "#b80012" }}>
          {accepted ? "Accepted" : `Rejected${verdict.reason ? `: ${verdict.reason}` : ""}`}
        </span>
        {!disabled && (
          <McButton
            appearance="neutral"
            variant="plain"
            fit="small"
            label="Change"
            onClick={() => setChanging(true)}
          />
        )}
      </div>
    );
  }

  if (rejecting) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxWidth: "380px" }}>
        <select
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          aria-label="Reason for rejecting this match"
          style={{ padding: "6px", fontSize: "13px" }}
        >
          <option value="">Select a reason…</option>
          {REJECT_REASONS.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        <input
          type="text"
          value={note}
          maxLength={300}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Optional note"
          aria-label="Optional note"
          style={{ padding: "6px", fontSize: "13px" }}
        />
        <div style={{ display: "flex", gap: "8px" }}>
          <McButton
            appearance="primary"
            fit="small"
            label="Confirm reject"
            disabled={disabled || !reason}
            onClick={() => {
              onReject(match, reason, note.trim());
              setRejecting(false);
              setChanging(false);
              setReason("");
              setNote("");
            }}
          />
          <McButton
            appearance="neutral"
            variant="outlined"
            fit="small"
            label="Cancel"
            onClick={() => setRejecting(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: "8px" }}>
      <McButton
        appearance="primary"
        variant="outlined"
        fit="small"
        label="Accept"
        disabled={disabled}
        onClick={() => {
          onAccept(match);
          setChanging(false);
        }}
      />
      <McButton
        appearance="neutral"
        variant="outlined"
        fit="small"
        label="Reject"
        disabled={disabled}
        onClick={() => setRejecting(true)}
      />
    </div>
  );
}

export default RecurrenceMatchFeedback;
